import { useLanguage } from '@context'
import { useImagePicker } from '@hooks'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import React, { ReactNode, useState } from 'react'
import { Image, Platform, StyleSheet, TouchableOpacity, TouchableOpacityProps, View } from 'react-native'
import { Icon, Text } from '../atoms'
import { BottomModal, OptionsBottomModal } from './BottomModal'

export type ImageUploadPropsBase = TouchableOpacityProps & {
  onImageSelect: (imageUri: string) => void
  children: ReactNode
}

export type ImageUploadProps = {
  title: string
  onImageSelect: (imageUri: string) => void
}

export const ImageUploadBase = ({
  onImageSelect,
  children,
  ...props
}: ImageUploadPropsBase): React.ReactElement => {
  const { t } = useLanguage()
  const [isVisible, setIsVisible] = useState(false)

  const { openCamera, openImageLibrary } = useImagePicker((uri) => {
    setIsVisible(false)
    onImageSelect(uri)
  })

  const handleWebFileSelect = () => {
    if (Platform.OS !== 'web') return

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            onImageSelect(reader.result)
          }
        }
        reader.readAsDataURL(file)
      }
    }
    input.click()
  }

  const handlePress = () => {
    if (Platform.OS === 'web') {
      handleWebFileSelect()
    } else {
      setIsVisible(true)
    }
  }

  const optionsImage: OptionsBottomModal[] = [
    {
      iconName: 'picture',
      title: t('imageUpload.fromCamera'),
      onPress: openCamera,
    },
    {
      iconName: 'upload',
      title: t('imageUpload.fromGallery'),
      onPress: openImageLibrary,
    },
  ]

  return (
    <>
      <TouchableOpacity
        {...props}
        onPress={handlePress}
      >
        {children}
      </TouchableOpacity>

      {Platform.OS !== 'web' && (
        <BottomModal
          {...{
            isVisible,
            options: optionsImage,
            setIsVisible,
          }}
        />
      )}
    </>
  )
}

export const ImageUpload = ({ title, onImageSelect }: ImageUploadProps): React.ReactElement => {
  const theme = useTheme()
  const styles = getStyle(theme)

  const [image, setImage] = useState('')

  return (
    <View style={styles.container}>
      <View style={styles.container}>
        <Text type="bodySmall" color={theme.colors.lightGrey} style={{ textAlign: 'center' }}>
          {title}
        </Text>

        <ImageUploadBase
          onImageSelect={(image) => {
            setImage(image)
            onImageSelect(image)
          }}
          style={styles.imageContainer}
        >
          {image ? (
            <Image source={{ uri: image }} style={styles.image} />
          ) : (
            <Icon name="uploadCloud" />
          )}
        </ImageUploadBase>
      </View>
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      alignItems: 'center',
    },
    imageContainer: {
      width: 100,
      height: 100,
      borderRadius: 50,
      backgroundColor: theme.colors.grey,
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: theme.spacing.md,
    },
    image: {
      height: '100%',
      width: '100%',
      borderRadius: 50,
    },
  })
